import React, { useEffect, useRef, useState } from 'react'

import { BeltVisual } from '@goodfellas/belt-visual'
import * as Belt from '@goodfellas/graduation'

import { goodfellasApi } from '../../../apis'
import {
  normalizeBeltColor,
  normalizeDegree,
} from '../../../functions/graduation'

interface Opponent {
  identificator?: {
    type: string
    uuid: string
  } | null
  name: string
  firstName?: string | null
  lastName?: string | null
  avatar?: string | null
  academy?: string | null
  /** Número completo — o servidor só o envia para administradores. */
  phoneNumber?: string | null
  /** Fim do número (`••• 7777`), que todo mundo recebe. */
  phoneMasked?: string | null
  belt?: {
    color: string
    degree: number
  } | null
}

/** Nome e sobrenome; o apelido só entra quando não há nome cadastrado. */
function displayName(opponent: Opponent): string {
  return (
    [opponent.firstName, opponent.lastName].filter(Boolean).join(' ').trim() ||
    opponent.name
  )
}

/**
 * `(11) 99999-9999` a partir de E.164 ou de dígitos soltos — as duas formas
 * existem na base. Número estrangeiro sai como veio, sem forçar formato BR.
 */
function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  const local =
    digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits
  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`
  }
  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`
  }
  return phone
}

/** O completo quando o servidor manda (admin); senão o fim do número. */
function phoneLabel(opponent: Opponent): string | undefined {
  if (opponent.phoneNumber) return formatPhone(opponent.phoneNumber)
  return opponent.phoneMasked ?? undefined
}

/** Map API response (profileUserUuid, belt string) to Opponent shape for match form */
function mapSearchResultToOpponent(item: {
  name: string
  firstName?: string
  lastName?: string
  avatar?: string
  phoneNumber?: string
  phoneMasked?: string
  profileUserUuid?: string
  academy?: string
  belt?: string
  beltDegree?: number
}): Opponent {
  const identificator =
    item.profileUserUuid != null
      ? { type: 'profile_user' as const, uuid: item.profileUserUuid }
      : null
  const belt =
    item.belt != null
      ? typeof item.belt === 'string'
        ? { color: item.belt, degree: item.beltDegree ?? 0 }
        : {
            color: (item.belt as any).color ?? '',
            degree: (item.belt as any).degree ?? 0,
          }
      : null
  return {
    name: item.name,
    firstName: item.firstName ?? null,
    lastName: item.lastName ?? null,
    avatar: item.avatar ?? null,
    academy: item.academy ?? null,
    phoneNumber: item.phoneNumber ?? null,
    phoneMasked: item.phoneMasked ?? null,
    belt,
    identificator,
  }
}

/**
 * Uma pessoa na lista: foto, nome, academia · telefone e faixa. O mesmo
 * desenho serve para a opção do dropdown e para o cartão de quem já foi
 * escolhido — escolher alguém não deve mudar o que se vê dela.
 */
function OpponentRow({ opponent }: { opponent: Opponent }) {
  return (
    <>
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
        {opponent.avatar ? (
          <img
            src={opponent.avatar}
            alt=""
            className="object-cover w-full h-full block"
          />
        ) : (
          <span className="text-sm font-medium text-gray-600">
            {displayName(opponent).charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="flex-grow min-w-0 text-left">
        <div className="font-medium text-gray-900 truncate">
          {displayName(opponent)}
        </div>
        {(opponent.academy || phoneLabel(opponent)) && (
          <div className="text-sm text-gray-500 truncate">
            {[opponent.academy, phoneLabel(opponent)]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
      </div>
      {opponent.belt && (
        <div className="flex-shrink-0 w-16">
          <BeltVisual
            color={normalizeBeltColor(opponent.belt) || Belt.Colors.White}
            degree={normalizeDegree(
              normalizeBeltColor(opponent.belt),
              opponent.belt.degree,
            )}
            size="sm"
          />
        </div>
      )}
    </>
  )
}

interface OpponentSearchProps {
  value: string
  onChange: (value: string) => void
  onSelect: (opponent: Opponent | null) => void
  placeholder?: string
  hasError?: boolean
  className?: string
  /**
   * Semeia o oponente já vinculado ao abrir em modo edição (#1292). Sem isto o
   * estado interno começa vazio: o selo "Oponente vinculado" não aparece, a
   * busca dispara sozinha (o campo já vem preenchido) e a primeira tecla
   * digitada desvincula o perfil sem o usuário perceber.
   */
  initialSelected?: Opponent | null
}

const OpponentSearch = React.forwardRef<HTMLInputElement, OpponentSearchProps>(
  (
    {
      value,
      onChange,
      onSelect,
      placeholder = 'Buscar oponente por nome ou telefone...',
      hasError = false,
      className,
      initialSelected = null,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [results, setResults] = useState<Opponent[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedOpponent, setSelectedOpponent] = useState<Opponent | null>(
      initialSelected,
    )
    const [hasSearched, setHasSearched] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    // Close dropdown when clicking outside
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Search opponents
    useEffect(() => {
      // Don't search if an opponent is already selected
      if (selectedOpponent) {
        return
      }

      const searchOpponents = async () => {
        if (value.length < 2) {
          setResults([])
          setHasSearched(false)
          return
        }

        setLoading(true)
        try {
          const response = await goodfellasApi.get(
            `/competition/opponents/search?q=${encodeURIComponent(value)}`,
          )

          if (response.data.success && Array.isArray(response.data.data)) {
            setResults(
              response.data.data.map((item: any) =>
                mapSearchResultToOpponent(item),
              ),
            )
            setIsOpen(true)
            setHasSearched(true)
          }
        } catch (err) {
          console.error('Error searching opponents:', err)
          setResults([])
          //setHasSearched(true)
        } finally {
          setLoading(false)
        }
      }

      const debounce = setTimeout(searchOpponents, 300)
      return () => clearTimeout(debounce)
    }, [value, selectedOpponent])

    const handleSelect = (opponent: Opponent) => {
      setSelectedOpponent(opponent)
      // O que fica gravado na luta é o mesmo nome que a lista mostrou.
      onChange(displayName(opponent))
      onSelect(opponent)
      setIsOpen(false)
      setHasSearched(false)
    }

    // Desvincula o perfil mas deixa o nome escrito: quem aperta o X quer
    // trocar de pessoa ou corrigir o nome, não recomeçar do zero.
    const handleClear = () => {
      setSelectedOpponent(null)
      onSelect(null)
      setHasSearched(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
      setSelectedOpponent(null)
      onSelect(null)
    }

    return (
      <div ref={wrapperRef} className="relative">
        {selectedOpponent ? (
          // Escolhido, o competidor deixa de ser um nome digitado e passa a ser
          // a mesma linha que a busca mostrou. O X o devolve ao campo de texto.
          <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl flex items-center gap-3">
            <OpponentRow opponent={selectedOpponent} />
            <button
              type="button"
              onClick={handleClear}
              aria-label="Trocar competidor"
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={value}
              onChange={handleInputChange}
              onFocus={() => value.length >= 2 && setIsOpen(true)}
              placeholder={placeholder}
              ref={ref}
              aria-invalid={hasError}
              className={[
                'w-full px-4 py-3 pr-10 bg-gray-50 border rounded-xl outline-none transition-all duration-200 text-gray-900 placeholder:text-gray-400',
                hasError
                  ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                  : 'border-gray-200 focus:ring-2 focus:ring-red-500',
                className,
              ]
                .filter(Boolean)
                .join(' ')}
            />
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
              </div>
            )}
          </div>
        )}

        {/* Selected opponent badge */}
        {selectedOpponent && (
          <div className="mt-2 flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              Oponente vinculado:{' '}
              <strong>{displayName(selectedOpponent)}</strong>
              {selectedOpponent.academy && ` (${selectedOpponent.academy})`}
            </span>
          </div>
        )}

        {/* Dropdown results */}
        {isOpen && results.length > 0 && !selectedOpponent && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {results.map((opponent, index) => (
              <button
                key={
                  opponent.identificator?.uuid ??
                  opponent.name + (opponent.academy ?? '') + index
                }
                type="button"
                onClick={() => handleSelect(opponent)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
              >
                <OpponentRow opponent={opponent} />
              </button>
            ))}
          </div>
        )}

        {/* No results message */}
        {isOpen &&
          value.length >= 2 &&
          results.length === 0 &&
          !loading &&
          !selectedOpponent &&
          hasSearched && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
              <p className="text-sm">Nenhum oponente encontrado</p>
              <p className="text-xs mt-1">
                O nome será registrado como novo oponente
              </p>
            </div>
          )}
      </div>
    )
  },
)

OpponentSearch.displayName = 'OpponentSearch'

export default OpponentSearch
